import { ApiError } from "../api.ts";
import { clear, errorBox, h, link } from "../dom.ts";
import { navigate, setTitle } from "../router.ts";
import { signedIn, startSignIn, tokenFromInput, verifySignIn, type Notice } from "../session.ts";

/** Only same-site paths are allowed after sign-in. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

/** What a magic-link landing ended in, from accounts' error code and what this browser knows. */
export type LandingState = "used" | "expired" | "invalid" | "signed_in" | "bad_link" | "rate_limited" | "error";

/**
 * accounts (wave 6) answers `401 link_expired` and `401 link_used`, and `401 invalid_token` only for a link it doesn't
 * know. Older builds sent `invalid_token` for all three, so a link this browser already used still reads as used.
 */
export function landingState(code: string | null, ctx: { usedHere: boolean; signedIn: boolean }): LandingState {
  const used = code === "link_used" || (code === "invalid_token" && ctx.usedHere);
  if (used || code === "invalid_token" || code === "link_expired") {
    if (ctx.signedIn) return "signed_in";
    if (used) return "used";
    if (code === "link_expired") return "expired";
    return "invalid";
  }
  if (code === "rate_limited") return "rate_limited";
  if (code === "bad_link") return "bad_link";
  return "error";
}

const USED_KEY = "thrdm.usedLinks";

/** A short hash of a link token, so the browser can recognise a link it already used without keeping the token. */
async function linkHash(token: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return [...new Uint8Array(d).slice(0, 12)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function usedLinks(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(USED_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function rememberUsed(hash: string): void {
  try {
    localStorage.setItem(USED_KEY, JSON.stringify([hash, ...usedLinks().filter((x) => x !== hash)].slice(0, 20)));
  } catch {
    /* storage off: we just can't say "already used" later */
  }
}

/**
 * The landing from a web magic link (/signin?token=ml_…). The token leaves the address bar at once, and one
 * verify runs per token even when the page re-renders (signing in re-renders every page).
 */
let landing: { token: string; next: string; el: HTMLElement; done: boolean } | null = null;

function landingPage(token: string, next: string): HTMLElement {
  if (landing?.token === token) return landing.el;
  const box = h("div", { class: "signin-box", "aria-live": "polite" }, h("p", null, "Signing you in…"));
  const el = h("div", { class: "signin" }, h("h1", null, "Sign in"), box);
  const me: { token: string; next: string; el: HTMLElement; done: boolean } = { token, next, el, done: false };
  landing = me;
  const valid = tokenFromInput(token);
  void (async () => {
    await run();
    me.done = true;
  })();
  async function run(): Promise<void> {
    const hash = valid ? await linkHash(valid).catch(() => null) : null;
    if (!valid) return show(box, "bad_link", next);
    try {
      const v = await verifySignIn(valid);
      if (hash) rememberUsed(hash);
      if (v.created && v.notices.length) {
        // a new account: accounts' sign-up notices must be shown before moving on
        clear(
          box,
          h("h2", null, "Welcome to Banger"),
          h("p", null, "Your account is ready and you're signed in."),
          v.notices.map((n) => h("div", { class: "note", role: "note" }, h("strong", null, n.title), h("p", { class: "small", style: { margin: "4px 0 0" } }, n.message))),
          link(next, { class: "btn" }, "Continue"),
        );
        return;
      }
      clear(box, h("p", null, "You're signed in."), link(next, { class: "btn" }, "Continue"));
      navigate(next, true);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      const state = landingState(code, { usedHere: !!hash && usedLinks().includes(hash), signedIn: signedIn() });
      show(box, state, next, e);
    }
  }
  return el;
}

function show(box: HTMLElement, state: LandingState, next: string, e?: unknown): void {
  const again = h("p", null, link(`/signin${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`, { class: "btn" }, "Send me a new link"));
  const why = h("p", { class: "dim small" }, "Sign-in links work once and expire 15 minutes after we send them.");
  const views: Record<LandingState, () => (HTMLElement | null)[]> = {
    signed_in: () => [h("h2", null, "You're already signed in"), h("p", null, "That link can't be used again, but this browser is signed in."), link(next, { class: "btn" }, "Continue")],
    used: () => [h("h2", null, "This link was already used"), h("p", null, "You signed in with it before, and each link works only once. Ask for a new one to sign in again."), why, again],
    expired: () => [h("h2", null, "This link has expired"), h("p", null, "Ask for a new one and open it within 15 minutes."), why, again],
    invalid: () => [h("h2", null, "This link doesn't work"), h("p", null, "We don't recognise it. Copy the whole link from the latest email, or ask for a new one; it arrives in a minute."), why, again],
    bad_link: () => [h("h2", null, "That link looks incomplete"), h("p", null, "Copy the whole link from the email, or ask for a new one."), again],
    rate_limited: () => [h("h2", null, "Too many tries"), h("p", null, "Please wait a few minutes, then open the link again."), link("/signin", { class: "btn ghost" }, "Back to sign in")],
    error: () => [h("h2", null, "We couldn't sign you in"), e ? errorBox(e) : null, h("p", { class: "dim small" }, "Open the link from the email again, or ask for a new one."), again],
  };
  clear(box, views[state]());
}

export function signinPage(_p: unknown, query: URLSearchParams): HTMLElement {
  setTitle("Sign in");
  const next = safeNext(query.get("next"));
  const box = h("div", { class: "signin-box" });

  // A web magic link (/signin?token=ml_…) signs in directly; the token leaves the address bar (and history) at once.
  const token = query.get("token");
  if (token !== null) {
    history.replaceState(null, "", `/signin${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`);
    return landingPage(token, next);
  }
  // the re-render that signing in triggers while the landing is still at work (the token is gone from the address)
  if (landing && !landing.done) return landing.el;
  landing = null;

  if (signedIn()) {
    clear(box, h("p", null, "You're signed in. "), link(next, { class: "btn" }, "Continue"));
    return h("div", { class: "signin" }, h("h1", null, "Sign in"), box);
  }

  const err = h("div", { "aria-live": "polite" });
  const birth = h("div", { class: "field", hidden: true }, h("label", { for: "birth" }, "Date of birth"), h("input", { id: "birth", name: "birth", type: "date" }), h("p", { class: "dim small" }, "Banger is for people 13 and older. We keep only your age band, never the date."));
  const email = h("input", { id: "email", name: "email", type: "email", required: true, autocomplete: "email", placeholder: "you@example.com" });
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        clear(err);
        const b = (birth.querySelector("input") as HTMLInputElement).value || undefined;
        try {
          const r = await startSignIn(email.value.trim(), b);
          sent(email.value.trim(), r.dev?.token ?? null, r.newAccount, r.notices ?? []);
        } catch (x) {
          if (x instanceof ApiError && x.code === "birth_date_required") {
            birth.hidden = false;
            (birth.querySelector("input") as HTMLInputElement).required = true;
            clear(err, h("p", { class: "note" }, "New here? Add your date of birth to create your account."));
          } else if (x instanceof ApiError && x.code === "under_age") {
            clear(err, h("p", { class: "error", role: "alert" }, "Sorry, Banger is for people 13 and older."));
          } else clear(err, errorBox(x));
        }
      },
    },
    h("div", { class: "field" }, h("label", { for: "email" }, "Email"), email),
    birth,
    h("button", { class: "btn", type: "submit" }, "Email me a sign-in link"),
    err,
  );

  const sent = (address: string, devToken: string | null, isNew: boolean, notices: Notice[]) => {
    const paste = h("input", { id: "paste", placeholder: "Paste the link or code", autocomplete: "off" });
    const perr = h("div", { "aria-live": "polite" });
    const go = async (t: string | null) => {
      if (!t) return clear(perr, h("p", { class: "error" }, "That doesn't look like a sign-in link."));
      try {
        await verifySignIn(t);
        navigate(next, true);
      } catch (x) {
        clear(perr, errorBox(x));
      }
    };
    clear(
      box,
      h("h2", null, "Check your email"),
      h("p", null, `We sent a sign-in link to ${address}. It works once and expires in 15 minutes.`),
      isNew ? h("p", { class: "dim small" }, "Opening it creates your account.") : null,
      notices.map((n) => h("div", { class: "note", role: "note" }, h("strong", null, n.title), h("p", { class: "small", style: { margin: "4px 0 0" } }, n.message))),
      devToken
        ? h(
            "div",
            { class: "note" },
            h("p", null, h("strong", null, "Dev mode: "), "no email is sent; accounts returned the link."),
            h("button", { class: "btn", type: "button", onclick: () => go(devToken) }, "Open the sign-in link"),
          )
        : null,
      h(
        "form",
        { class: "stack", onsubmit: (e: Event) => (e.preventDefault(), go(tokenFromInput(paste.value))) },
        h("label", { for: "paste" }, "Or paste the link here"),
        paste,
        h("button", { class: "btn ghost", type: "submit" }, "Sign in"),
        perr,
      ),
    );
  };

  clear(box, form);
  return h(
    "div",
    { class: "signin" },
    h("h1", null, "Sign in"),
    h("p", { class: "lead" }, "One account for the website and the Banger app: your library, follows and avatar go with you."),
    box,
  );
}
